import React, { useEffect, useState } from "react";
import _groupBy from "lodash/groupBy";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../components/Layout";
import Container from "../../../../components/Container";
import Flex from "../../../../components/Flex";
import Content from "../../../../components/Content";
import Button from "../../../../components/Button";
import ButtonList from "../../../../components/ButtonList";
import LinkButton from "../../../../components/LinkButton";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../components/MessageProvider";
import Modal from "../../../../components/ModalPopup";
import DataTable, {
  DropDownListFilter,
  SelectInputFilter
} from "../../../../components/DataTable";
import { useAuthState } from "../../../../components/AuthProvider";
import Tooltip from "../../../../components/Tooltip";
import Spinner from "../../../../components/Loader/Spinner";

import styles from "./ViewUser.module.scss";

const ViewUser = () => {
  // TODO: handle scenario for showing self user account!
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const userApiUrl = `${clientConfigApiDomain}/users`;
  const userRolesApiUrl = `${clientConfigApiDomain}/user_roles`;
  const userRolesRpcUrl = `${clientConfigApiDomain}/rpc/user_roles`;
  const { id } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [userData, setUserData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [userAccessData, setUserAccessData] = useState([]);
  const [loadedUserIsAdmin, setLoadedUserIsAdmin] = useState(false);

  const [showDeleteAccessDialog, setShowDeleteAccessDialog] = React.useState(
    false
  );
  const [selectedUserAccess, setSelectedUserAccess] = React.useState({});
  const [userClientsFilter, setUserClientsFilter] = useState([]);

  const handleDeleteSuccess = userAccess => {
    toast.success(
      `Successfully deleted ${userAccess.role_name} role access to ${userAccess.client_name} for this user.`
    );
    setUserAccessData(userAccessData.filter(ua => ua !== userAccess));
  };

  const handleDeleteError = userAccess => {
    toast.error(
      `Error when attempting to delete ${userAccess.role_name} role access to ${userAccess.client_name} for this user.`
    );
  };

  const onDeleteUserAccess = () => {
    fetch(`${userRolesApiUrl}/${selectedUserAccess.id}/`, { method: "delete" })
      .then(response => {
        if (!response.ok) throw Error("Failed to delete user access.");
        handleDeleteSuccess(selectedUserAccess);
      })
      .catch(() => {
        handleDeleteError(selectedUserAccess);
      });
    setShowDeleteAccessDialog(false);
  };

  const onDeleteUserAccessCancelClick = () => {
    setShowDeleteAccessDialog(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const getUserData = async () => {
      const response = await fetch(`${userApiUrl}/${id}/`);
      const data = await response.json();
      setUserData(data);
    };
    const getUserAccess = async () => {
      const response = await fetch(`${userRolesRpcUrl}/?user__id=${id}`);
      if (response.status === 200) {
        const data = await response.json();
        setUserAccessData(data.results);
      }
    };
    const createUserClientsOrganizationsFilter = () => {
      const userClientMapped = user.clients.map(client => ({
        client: client.name,
        organization: client.organization.name
      }));
      const userClientGroupByOrg = _groupBy(userClientMapped, `organization`);
      const userClient = Object.keys(userClientGroupByOrg).map(org => ({
        text: org,
        children: userClientGroupByOrg[org].map(childValues => ({
          text: childValues.client,
          value: childValues.client
        }))
      }));

      setUserClientsFilter(userClient);
    };
    if (!hasLoaded && !isLoading) {
      setIsLoading(true);
      Promise.all([
        getUserData(),
        getUserAccess(),
        createUserClientsOrganizationsFilter()
      ]).then(() => {
        const isAdmin = userAccessData
          .map(uad => uad.role.name)
          .some(role => role.includes("Global Admin"));
        setLoadedUserIsAdmin(isAdmin);

        setIsLoading(false);
        setHasLoaded(true);
      });
    }
  }, [
    setIsLoading,
    setHasLoaded,
    setUserClientsFilter,
    hasLoaded,
    userApiUrl,
    id,
    userRolesRpcUrl,
    user.clients,
    userAccessData,
    isLoading
  ]);

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedUserAccess }
    }
  }) => {
    const onDetailsClick = () => {
      history.push({
        pathname: `/admin/users/${id}/access/${selectedUserAccess.id}`,
        state: { selectedUserAccess }
      });
    };

    const onEditClick = () => {
      history.push({
        pathname: `/admin/users/${id}/access/${selectedUserAccess.id}/edit/`,
        state: { selectedUserAccess }
      });
    };

    const onDeleteClick = () => {
      selectedUserAccess.client_name = selectedUserAccess.client.name;
      selectedUserAccess.role_name = selectedUserAccess.role.name;
      setSelectedUserAccess(selectedUserAccess);
      setShowDeleteAccessDialog(true);
    };

    const isVisible = selectedUser => {
      return (
        user.role === "Global Admin" ||
        //selectedUser.role.name !== "Global Admin" ||
        window.localStorage.getItem("LOGIN_TYPE") !== "MS-Email"
      );
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        {/* User cant edit or delete his own access */}
        {user.id !== userData.id && isVisible(selectedUserAccess) && (
          <LinkButton icon="edit" onClick={onEditClick}>
            Edit
          </LinkButton>
        )}
        {user.id !== userData.id && isVisible(selectedUserAccess) && (
          <LinkButton icon="remove" onClick={onDeleteClick}>
            Delete
          </LinkButton>
        )}
      </ButtonList>
    );
  };

  const userAccessColumns = [
    {
      Header: "Organization or Accounts",
      accessor: "client.name",
      Filter: SelectInputFilter,
      filter: SelectInputFilter.filter,
      filterProps: { items: userClientsFilter }
    },
    {
      Header: "Role",
      accessor: "role.name",
      Filter: DropDownListFilter,
      filter: "equals"
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = "Loading ...";
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item render={() => <Link to="/admin/users">Users</Link>} />
        <Breadcrumbs.Item
          last
          render={() => `User: ${userData.last_name}, ${userData.first_name}`}
        />
      </Breadcrumbs>
    );
  }

  const isEditVisible = selectedUser => {
    const selectedUserRoles = [
      ...new Set(selectedUser.roles.map(role => role.name))
    ];
    return (
      user.role === "Global Admin" ||
      !selectedUserRoles.includes("Global Admin")
    );
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>
                  User: {userData.last_name}, {userData.first_name}
                </h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>User Details</h3>
                  <div>
                    {isEditVisible(userData) ? (
                      <Button
                        className={styles.margin}
                        primary
                        onClick={() => {
                          history.push(`${id}/edit`);
                        }}
                      >
                        Edit
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className={styles.details}>
                  <div>
                    <h4>User ID</h4>
                    <span>{userData.id}</span>
                  </div>
                  <div>
                    <h4>Name</h4>
                    <span>
                      {userData.last_name}, {userData.first_name}
                    </span>
                  </div>
                  <div>
                    <h4>Email</h4>
                    <span>
                      <a href={"mailto:" + userData.email}>{userData.email}</a>
                    </span>
                  </div>
                  <div>
                    <h4>Created By</h4>
                    <span>{userData.created_by}</span>
                  </div>
                  <div>
                    <h4>Date Created</h4>
                    <span>
                      {moment(userData.created_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Edited By</h4>
                    <span>{userData.updated_by}</span>
                  </div>
                  <div>
                    <h4>Date Updated</h4>
                    <span>
                      {moment(userData.updated_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                </div>

                <div className={styles.relatedDetails}>
                  <h3>User Access</h3>
                  {user.id !== userData.id ? (
                    <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                      <LinkButton
                        icon="add"
                        onClick={() => {
                          history.push(`/admin/users/${id}/access/add`, [
                            userData.name
                          ]);
                        }}
                        disabled={loadedUserIsAdmin}
                      >
                        Add Access
                        {loadedUserIsAdmin ? (
                          <Tooltip>
                            This user already has administrative access. <br />
                            No additional access can be to be added for this
                            user.
                            <br />
                            Modify the existing access before you can add
                            additional access.
                          </Tooltip>
                        ) : null}
                      </LinkButton>
                    </ButtonList>
                  ) : null}
                  <div>
                    <DataTable
                      columns={userAccessColumns}
                      data={userAccessData}
                      hasSorting
                      hasPagination
                      hasFiltering
                    />
                  </div>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
      <Modal
        open={showDeleteAccessDialog}
        onClose={() => setShowDeleteAccessDialog(false)}
      >
        <div style={{ marginBottom: "1rem" }}>
          Are you sure you want to delete {selectedUserAccess.role_name} role
          access to {selectedUserAccess.client_name} for this user?
        </div>
        <ButtonList>
          <Button primary type="submit" onClick={onDeleteUserAccess}>
            Confirm
          </Button>
          <Button onClick={onDeleteUserAccessCancelClick}>Cancel</Button>
        </ButtonList>
      </Modal>
    </Layout>
  );
};

export default ViewUser;
